import { useEffect } from 'react';

import Loader from '~components/generic/loader/Loader';
import { DuplicateModalController } from '~extension/web2print/controller/DuplicateModalController';
import useMediaLibrarySearchQuery from '~modules/media-library/context/useMediaLibrarySearchQuery';
import useMediaLibrarySettings from '~modules/media-library/context/useMediaLibrarySettings';
import { CropModalController } from '~modules/media-library/controller/CropModalController';
import { DetailModalController } from '~modules/media-library/controller/DetailModalController';
import { RaiseRequestModalController } from '~modules/media-library/controller/RaiseRequestModalController';
import ResultHeaderController from '~modules/media-library/controller/ResultHeaderController';
import ResultListController from '~modules/media-library/controller/ResultListController';
import { ShareModalController } from '~modules/media-library/controller/ShareModalController';
import useAssetActions from '~modules/media-library/controller/useAssetActions';
import { ZoomModalController } from '~modules/media-library/controller/ZoomModalController';
import { CollectionHeader } from '~modules/media-library/view/CollectionHeader/CollectionHeader';
import { LoadingError } from '~modules/media-library/view/LoadingError';
import { ShareAction } from '~modules/media-library/view/ShareModal/ShareAction';

type MediaLibraryProps = {
  searchTerm: string;
};

export function MediaLibraryResults({ searchTerm }: MediaLibraryProps) {
  const { isError, isLoading } = useMediaLibrarySettings();
  const { onSetSearchTerm } = useMediaLibrarySearchQuery();

  const {
    idDetails,
    idDetailsNext,
    idDetailsPrev,
    idDownload,
    closeDetails,
    closeDownload,
    idZoom,
    closeZoom,
    idAddToMyfiles,
    closeAddToMyfiles,
    idCrop,
    closeCrop,
    idWeb2Print,
    closeWeb2Print,
    idRaiseRequestForm,
    closeRaiseRequest,
    ...AssetActions
  } = useAssetActions();

  useEffect(() => {
    onSetSearchTerm(searchTerm);
  }, [onSetSearchTerm, searchTerm]);

  return (
    <div className="ml--search-view bg-gray w-full min-h-screen relative">
      {isError || isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          {isError && <LoadingError />}
          {isLoading && (
            <div className="relative w-16 h-8">
              <Loader absoluteCenter={true} />
            </div>
          )}
        </div>
      ) : (
        <>
          <CollectionHeader />
          <div className="relative top-0 z-20 flex flex-col">
            <ResultHeaderController />
          </div>
          <ResultListController AssetActions={AssetActions} />
        </>
      )}
      <DetailModalController
        assetId={idDetails}
        onClose={closeDetails}
        AssetActions={AssetActions}
        nextAssetId={idDetailsNext}
        prevAssetId={idDetailsPrev}
      />
      <ShareModalController assetId={idDownload} onClose={closeDownload} shareAction={ShareAction.Download} />
      <ShareModalController
        assetId={idAddToMyfiles}
        onClose={closeAddToMyfiles}
        shareAction={ShareAction.AddToMyfiles}
      />
      <ZoomModalController assetId={idZoom} onClose={closeZoom} />
      <CropModalController assetId={idCrop} onClose={closeCrop} />
      <DuplicateModalController assetId={idWeb2Print} onClose={closeWeb2Print} />
      <RaiseRequestModalController assetId={idRaiseRequestForm} onClose={closeRaiseRequest} />
    </div>
  );
}
